/**
 * Geo helpers for tasting notes: distances, geohash cache cells, candidate ranking and the
 * "continue your visit" rule. Pure functions; coordinates are decimal degrees.
 */
const EARTH_RADIUS_M = 6_371_008.8;
const toRad = (deg) => (deg * Math.PI) / 180;
export function isValidLatLng(point) {
    return (Number.isFinite(point.lat) &&
        Number.isFinite(point.lng) &&
        point.lat >= -90 &&
        point.lat <= 90 &&
        point.lng >= -180 &&
        point.lng <= 180);
}
/** Great-circle distance in metres (haversine). */
export function haversineMeters(a, b) {
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}
const GEOHASH_ALPHABET = '0123456789bcdefghjkmnpqrstuvwxyz';
/** Standard geohash. Precision 7 ≈ 153 m × 153 m cells, used as the lookup cache key. */
export function geohashEncode(point, precision = 7) {
    if (!isValidLatLng(point))
        throw new RangeError('Invalid coordinates');
    let latMin = -90;
    let latMax = 90;
    let lngMin = -180;
    let lngMax = 180;
    let hash = '';
    let bit = 0;
    let ch = 0;
    let even = true;
    while (hash.length < precision) {
        if (even) {
            const mid = (lngMin + lngMax) / 2;
            if (point.lng >= mid) {
                ch = (ch << 1) | 1;
                lngMin = mid;
            }
            else {
                ch <<= 1;
                lngMax = mid;
            }
        }
        else {
            const mid = (latMin + latMax) / 2;
            if (point.lat >= mid) {
                ch = (ch << 1) | 1;
                latMin = mid;
            }
            else {
                ch <<= 1;
                latMax = mid;
            }
        }
        even = !even;
        if (++bit === 5) {
            hash += GEOHASH_ALPHABET[ch];
            bit = 0;
            ch = 0;
        }
    }
    return hash;
}
/** Cache key for a nearby lookup; contains no user identifier. */
export function placesCacheKey(point, radiusM) {
    return `${geohashEncode(point, 7)}:${Math.round(radiusM)}`;
}
export function isValidBoundingBox(box) {
    return (isValidLatLng({ lat: box.south, lng: box.west }) &&
        isValidLatLng({ lat: box.north, lng: box.east }) &&
        box.south <= box.north);
}
/** True when the box crosses the antimeridian (west > east). */
export function crossesAntimeridian(box) {
    return box.west > box.east;
}
export function boundingBoxContains(box, point) {
    if (point.lat < box.south || point.lat > box.north)
        return false;
    return crossesAntimeridian(box)
        ? point.lng >= box.west || point.lng <= box.east
        : point.lng >= box.west && point.lng <= box.east;
}
/** Square box around a point, clamped to valid latitudes. */
export function boundingBoxAround(center, radiusM) {
    const dLat = (radiusM / EARTH_RADIUS_M) * (180 / Math.PI);
    const cos = Math.max(0.01, Math.cos(toRad(center.lat)));
    const dLng = dLat / cos;
    return {
        west: Math.max(-180, center.lng - dLng),
        south: Math.max(-90, center.lat - dLat),
        east: Math.min(180, center.lng + dLng),
        north: Math.min(90, center.lat + dLat),
    };
}
export const MAX_PLACE_CANDIDATES = 10;
/**
 * Orders venue candidates for "Are you at …?". Previously confirmed places come first,
 * then by distance adjusted for accuracy, then by raw distance, then by name so the
 * order is stable. Unnamed candidates are dropped.
 */
export function rankPlaceCandidates(position, candidates, options = {}) {
    const accuracy = Math.max(0, options.accuracyM ?? 0);
    const confirmed = new Set(options.confirmedRefs ?? []);
    const seen = new Set();
    const ranked = [];
    for (const candidate of candidates) {
        if (!candidate.name.trim() || seen.has(candidate.ref) || !isValidLatLng(candidate))
            continue;
        seen.add(candidate.ref);
        const distanceM = haversineMeters(position, candidate);
        ranked.push({
            candidate,
            distanceM,
            effectiveDistanceM: Math.max(0, distanceM - accuracy),
            previouslyConfirmed: confirmed.has(candidate.ref),
        });
    }
    ranked.sort((a, b) => Number(b.previouslyConfirmed) - Number(a.previouslyConfirmed) ||
        a.effectiveDistanceM - b.effectiveDistanceM ||
        a.distanceM - b.distanceM ||
        a.candidate.name.localeCompare(b.candidate.name));
    return ranked.slice(0, options.limit ?? MAX_PLACE_CANDIDATES);
}
// ---------------------------------------------------------------------------
// Visit continuation
// ---------------------------------------------------------------------------
export const VISIT_CONTINUATION_MAX_AGE_MS = 4 * 60 * 60 * 1000;
export const VISIT_CONTINUATION_MAX_DISTANCE_M = 200;
/** Positions less accurate than this are not used for automatic decisions. */
export const POOR_ACCURACY_M = 200;
/**
 * A new dish continues an existing visit when that visit started less than 4 hours ago
 * and lies within 200 m of the current position – or when either side has no position.
 */
export function canContinueVisit(visit, now, position, placePosition = null) {
    const started = new Date(visit.visitedAt).getTime();
    const age = now.getTime() - started;
    if (!Number.isFinite(age) || age < 0 || age >= VISIT_CONTINUATION_MAX_AGE_MS)
        return false;
    if (!position)
        return true;
    const anchors = [];
    if (visit.lat !== null && visit.lng !== null)
        anchors.push({ lat: visit.lat, lng: visit.lng });
    if (placePosition)
        anchors.push(placePosition);
    if (!anchors.length)
        return true;
    return anchors.some((a) => haversineMeters(a, position) <= VISIT_CONTINUATION_MAX_DISTANCE_M);
}
/** Picks the most recent visit that can be continued, or null. */
export function findContinuableVisit(visits, now, position) {
    const sorted = [...visits].sort((a, b) => new Date(b.visitedAt).getTime() - new Date(a.visitedAt).getTime());
    return sorted.find((v) => canContinueVisit(v, now, position, v.place ?? null)) ?? null;
}
//# sourceMappingURL=geo.js.map